// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useMemo } from 'react';
import { RouteType } from '@Types';
import RouteBuilder from '@Services/RouteBuilder';
import { useLocalMessage } from '@Helpers/locale';
import client from '@Client';
import { handleNotificationError } from '@Helpers/notification';
import useUserPermissions from '@Hooks/useUserPermissions';
import { AdminPermissionsEnum, WebhookOutput, WebhookType } from 'thehive-sdk';
import { THLayout } from '@Molecules/THLayout';
import { THList } from '@Molecules/THList';
import { useGenericQuery } from '@Queries/genericQueries';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { EndpointManage } from '@Organisms/Notification/NotificationManage/EndpointManage/EndpointManage';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { Image, Space } from 'antd';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { ICONS } from '@Constants/iconsApp';
import { StatusContext } from '@Context/StatusProvider';
import { useDrawer } from '@Hooks/useDrawer';

import './Endpoints.scss';

export const endpointLogos: Record<WebhookType, ReactElement> = {
    webhook: <Image preview={false} width="30" src="static/icons/webhooks.svg" />,
    mattermost: <Image preview={false} width="30" src="static/icons/mattermost.svg" />,
    slack: <Image preview={false} width="30" src="static/icons/slack.svg" />,
    teams: <Image preview={false} width="30" src="static/icons/teams.svg" />,
    http: <Image preview={false} width="30" src="static/icons/http.svg" />,
};

const Endpoints = (): ReactElement => {
    const i = useLocalMessage();

    const {
        data: endpoints,
        refetch: listRefetch,
        status,
    } = useGenericQuery({
        fetchCallback: () => client.NotificationController.listEndpoints(),
        key: ['list-all-organisation-endpoints'],
    });

    const { licenseCapabilities } = useContext(StatusContext);

    const notificationCapability = useMemo<string[]>(
        () => Object.keys(licenseCapabilities.notification || {}),
        [licenseCapabilities],
    );

    const endpointList = useMemo(() => endpoints?.organisation || [], [endpoints]);

    const endpointNames = useMemo(() => endpointList.map(({ name }) => name), [endpointList]);

    const hasPermissions = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_CONFIG] });
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer<WebhookOutput>({});

    const columns = columnsRenderer<WebhookOutput>([
        {
            title: <ColumnTitle>{i('common.name')}</ColumnTitle>,
            className: 'endpoint-list__column__name custom-table__cell',
            render: ({ name }) => <> {name}</>,
        },
        {
            title: <ColumnTitle>{i('common.type')}</ColumnTitle>,
            className: 'endpoint-list__column__type custom-table__cell',
            render: ({ type = 'webhook' }) => (
                <Space>
                    {endpointLogos[type]}
                    {type
                        .split('')
                        .map((char, index) => (index === 0 ? char.toUpperCase() : char))
                        .join('')}
                </Space>
            ),
        },
        {
            title: <ColumnTitle>{i('endpoint.list.url-token')}</ColumnTitle>,
            className: 'endpoint-list__column__url custom-table__cell',
            render: ({ url, token, type }) => {
                const maskedToken = ''.padStart(16, '*');
                return <>{type === 'teams' ? maskedToken : url || `${token?.substring(0, 16)}...`}</>;
            },
        },
        {
            className: 'endpoint-list__column__actions custom-table__cell',
            render: (endpoint): ReactElement => (
                <ActionsList
                    actionList={{
                        edit: {
                            label: i('general.edit'),
                            icon: <ICONS.EditIcon />,
                            CustomComponent: (triggerElem) => (
                                <EndpointManage
                                    triggerElem={triggerElem}
                                    onFinish={({ type, ...endpoint }) =>
                                        client.NotificationController.addOrganisationEndpoint({
                                            ...endpoint,
                                            type: type?.name,
                                        })
                                            .then(() => listRefetch())
                                            .catch(handleNotificationError)
                                    }
                                    defaultValues={endpoint}
                                    notificationCapability={notificationCapability}
                                />
                            ),
                        },
                        delete: {
                            label: i('general.delete'),
                            icon: <ICONS.DeleteIcon />,
                            confirm: true,
                            message: i('endpoint.actions.delete.confirm'),
                            query: () => {
                                client.NotificationController.deleteOrganisationEndpoint(endpoint.name)
                                    .then(() => listRefetch())
                                    .catch(handleNotificationError);
                            },
                        },
                    }}
                    scope="single"
                    isIcon
                />
            ),
        },
    ]);

    return (
        <>
            <THLayout>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {hasPermissions && (
                                <AddButton label={i('endpoint.list.add')} onClick={() => setVisibleCreate(true)} />
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={endpointList.sort(({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB))}
                        columns={columns}
                        rowKey="name"
                        emptyPlaceholder={
                            <DisplayEmptyData spin={status === 'loading'} label={i('endpoint.empty')}>
                                {hasPermissions && (
                                    <THButton type="link" onClick={() => setVisibleCreate(true)}>
                                        {i('endpoint.list.add')}
                                    </THButton>
                                )}
                            </DisplayEmptyData>
                        }
                    />
                </THList>
            </THLayout>
            {visibleCreate && (
                <EndpointManage
                    endpointNames={endpointNames}
                    onFinish={({ type, ...endpoint }) =>
                        client.NotificationController.addOrganisationEndpoint({
                            ...endpoint,
                            type: type?.name,
                        })
                            .then(() => listRefetch())
                            .catch(handleNotificationError)
                    }
                    notificationCapability={notificationCapability}
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                />
            )}
        </>
    );
};

const pathUser = RouteBuilder.compute('organisation.endpoints');

export const Route: RouteType = {
    menu: {
        label: 'organisation.layout.nav-bar.endpoints',
        icon: <ICONS.NetworkWiredOutlined />,
    },

    path: pathUser,
    content: <Endpoints />,
    permissionNeeded: [AdminPermissionsEnum.MANAGE_CONFIG],
    shared: true,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.organisation.endpoints' },
};
